import { useCallback, useMemo, useState } from 'react';

import {
  CarescoutConsentConfig,
  ConsentConfig,
  consents,
  type ConsentItem,
} from '@shared/consents';

type ConsentUserType = 'advisor' | 'client' | 'partnerClient';

interface UseConsentSelectionProps {
  userType: ConsentUserType;
  consentType: 'submit' | 'start';
  isCareScout: boolean;
  requireConsentItems?: boolean;
}

export function useConsentSelection({
  userType,
  consentType,
  isCareScout,
  requireConsentItems = false,
}: UseConsentSelectionProps) {
  const consentItems = useMemo<ConsentItem[]>(() => {
    const config = isCareScout ? CarescoutConsentConfig : ConsentConfig;
    const consentIds =
      userType === 'advisor' ? config[consentType].advisor : config[consentType].client;

    return Object.values(consents).filter(consent => consentIds.includes(consent.id));
  }, [consentType, isCareScout, userType]);

  const [consentStates, setConsentStates] = useState<Record<string, boolean>>(() =>
    consentItems.reduce((states, item) => ({ ...states, [item.id]: false }), {}),
  );

  const setConsentChecked = useCallback((consentId: string, checked: boolean) => {
    setConsentStates(states => ({ ...states, [consentId]: checked }));
  }, []);

  const allConsentsChecked =
    (!requireConsentItems || consentItems.length > 0) &&
    consentItems.every(item => consentStates[item.id]);

  const checkedConsentIds = useMemo(
    () => consentItems.filter(item => consentStates[item.id]).map(item => item.id),
    [consentItems, consentStates],
  );

  return {
    consentItems,
    consentStates,
    setConsentChecked,
    allConsentsChecked,
    checkedConsentIds,
  };
}
